import { X, Zap, Phone, Clock, Sun, Dog, Accessibility, Users, Monitor, Leaf, Wind, Coffee, UtensilsCrossed, Car } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const venueTypes = [
  { value: "cafe", label: "Cafe" },
  { value: "pub", label: "Pub" },
  { value: "hotel", label: "Hotel" },
  { value: "library", label: "Library" },
  { value: "hub", label: "Hub" },
  { value: "other", label: "Other" },
];

const wifiOptions = [
  { value: "very_fast", label: "Very Fast" },
  { value: "fast", label: "Fast" },
  { value: "good", label: "Good" },
  { value: "poor", label: "Poor" },
];

const noiseOptions = [
  { value: "silent", label: "Silent" },
  { value: "quiet", label: "Quiet" },
  { value: "moderate", label: "Moderate" },
  { value: "lively", label: "Lively" },
];

// Quick filter presets for common use cases
const filterPresets = [
  { 
    id: "video_calls", 
    label: "Video Calls", 
    icon: Phone,
    filters: { good_for_calls: true, noise_level: "quiet" }
  },
  { 
    id: "long_sessions", 
    label: "Long Sessions", 
    icon: Clock,
    filters: { long_stay_friendly: true }
  },
  { 
    id: "team_meetup", 
    label: "Team Meetup", 
    icon: Users,
    filters: { group_friendly: true, has_meeting_rooms: true }
  },
  { 
    id: "pet_owner", 
    label: "Pet Friendly", 
    icon: Dog,
    filters: { pet_friendly: true }
  },
];

// Toggle button component for amenity filters
function ToggleFilter({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
        active
          ? "bg-brand text-white shadow-sm"
          : "bg-white border border-gray-200 text-gray-600 hover:border-brand hover:text-brand"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

export default function FilterPanel({ filters, onChange, onClear }) {
  // Handle preset click
  const applyPreset = (preset) => {
    Object.entries(preset.filters).forEach(([key, value]) => {
      onChange(key, value);
    });
  };

  // Check if a preset is active
  const isPresetActive = (preset) => {
    return Object.entries(preset.filters).every(([key, value]) => filters[key] === value);
  };

  return (
    <div className="max-w-6xl mx-auto mt-3 p-4 bg-gray-50 rounded-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Filters</h3>
        <button onClick={onClear} className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand" data-testid="clear-all-filters">
          <X className="w-3 h-3" /> Clear all
        </button>
      </div>

      {/* Quick Filter Presets */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-2">Quick Filters</p>
        <div className="flex flex-wrap gap-2">
          {filterPresets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => applyPreset(preset)}
              data-testid={`preset-${preset.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                isPresetActive(preset)
                  ? "bg-brand text-white shadow-md"
                  : "bg-white border border-gray-200 text-gray-700 hover:border-brand hover:shadow-sm"
              }`}
            >
              <preset.icon className="w-4 h-4" />
              {preset.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Main Filters Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <Select value={filters.venue_type || "all"} onValueChange={(v) => onChange("venue_type", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-venue-type" className="h-9 text-sm">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {venueTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">WiFi Speed</label>
          <Select value={filters.wifi_quality || "all"} onValueChange={(v) => onChange("wifi_quality", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-wifi" className="h-9 text-sm">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              {wifiOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Noise Level</label>
          <Select value={filters.noise_level || "all"} onValueChange={(v) => onChange("noise_level", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-noise" className="h-9 text-sm">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              {noiseOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Power Outlets</label>
          <Select value={filters.min_power_outlets?.toString() || "all"} onValueChange={(v) => onChange("min_power_outlets", v === "all" ? null : parseInt(v))}>
            <SelectTrigger data-testid="filter-power" className="h-9 text-sm">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              <SelectItem value="1">1+ outlets</SelectItem>
              <SelectItem value="3">3+ outlets</SelectItem>
              <SelectItem value="5">5+ outlets (Plenty)</SelectItem>
              <SelectItem value="10">10+ outlets (Many)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Min Rating</label>
          <Select value={filters.min_rating?.toString() || "all"} onValueChange={(v) => onChange("min_rating", v === "all" ? null : parseFloat(v))}>
            <SelectTrigger data-testid="filter-rating" className="h-9 text-sm">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              <SelectItem value="9">9+ Excellent</SelectItem>
              <SelectItem value="8">8+ Great</SelectItem>
              <SelectItem value="7">7+ Good</SelectItem>
              <SelectItem value="6">6+ Decent</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Featured</label>
          <Select value={filters.is_premium === null ? "all" : filters.is_premium.toString()} onValueChange={(v) => onChange("is_premium", v === "all" ? null : v === "true")}>
            <SelectTrigger data-testid="filter-premium" className="h-9 text-sm">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Venues</SelectItem>
              <SelectItem value="true">Featured Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Amenity Toggle Filters - Productivity */}
      <div className="mb-3">
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
          <Zap className="w-3 h-3" /> Productivity
        </p>
        <div className="flex flex-wrap gap-2">
          <ToggleFilter
            icon={Clock}
            label="Long Stays"
            active={filters.long_stay_friendly === true}
            onClick={() => onChange("long_stay_friendly", filters.long_stay_friendly ? null : true)}
            testId="filter-long-stay"
          />
          <ToggleFilter
            icon={Phone}
            label="Good for Calls"
            active={filters.good_for_calls === true}
            onClick={() => onChange("good_for_calls", filters.good_for_calls ? null : true)}
            testId="filter-calls"
          />
          <ToggleFilter
            icon={Users}
            label="Meeting Rooms"
            active={filters.has_meeting_rooms === true}
            onClick={() => onChange("has_meeting_rooms", filters.has_meeting_rooms ? null : true)}
            testId="filter-meeting-rooms"
          />
          <ToggleFilter
            icon={Monitor}
            label="Standing Desks"
            active={filters.has_standing_desks === true}
            onClick={() => onChange("has_standing_desks", filters.has_standing_desks ? null : true)}
            testId="filter-standing-desks"
          />
        </div>
      </div>

      {/* Amenity Toggle Filters - Space & Comfort */}
      <div className="mb-3">
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
          <Sun className="w-3 h-3" /> Space & Comfort
        </p>
        <div className="flex flex-wrap gap-2">
          <ToggleFilter
            icon={Sun}
            label="Natural Light"
            active={filters.natural_light === true}
            onClick={() => onChange("natural_light", filters.natural_light ? null : true)}
            testId="filter-natural-light"
          />
          <ToggleFilter
            icon={Wind}
            label="Air Conditioning"
            active={filters.has_ac === true}
            onClick={() => onChange("has_ac", filters.has_ac ? null : true)}
            testId="filter-ac"
          />
          <ToggleFilter
            icon={Leaf}
            label="Outdoor Seating"
            active={filters.has_outdoor_seating === true}
            onClick={() => onChange("has_outdoor_seating", filters.has_outdoor_seating ? null : true)}
            testId="filter-outdoor"
          />
          <ToggleFilter
            icon={Users}
            label="Spacious"
            active={filters.is_spacious === true}
            onClick={() => onChange("is_spacious", filters.is_spacious ? null : true)}
            testId="filter-spacious"
          />
        </div>
      </div>

      {/* Amenity Toggle Filters - Accessibility & Services */}
      <div>
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
          <Coffee className="w-3 h-3" /> Services & Access
        </p>
        <div className="flex flex-wrap gap-2">
          <ToggleFilter
            icon={UtensilsCrossed}
            label="Food Available"
            active={filters.has_food === true}
            onClick={() => onChange("has_food", filters.has_food ? null : true)}
            testId="filter-food"
          />
          <ToggleFilter
            icon={Dog}
            label="Pet Friendly"
            active={filters.pet_friendly === true}
            onClick={() => onChange("pet_friendly", filters.pet_friendly ? null : true)}
            testId="filter-pets"
          />
          <ToggleFilter
            icon={Accessibility}
            label="Accessible"
            active={filters.wheelchair_accessible === true}
            onClick={() => onChange("wheelchair_accessible", filters.wheelchair_accessible ? null : true)}
            testId="filter-accessible"
          />
          <ToggleFilter
            icon={Users}
            label="Group Friendly"
            active={filters.group_friendly === true}
            onClick={() => onChange("group_friendly", filters.group_friendly ? null : true)}
            testId="filter-groups"
          />
          <ToggleFilter
            icon={Car}
            label="Parking"
            active={filters.has_parking === true}
            onClick={() => onChange("has_parking", filters.has_parking ? null : true)}
            testId="filter-parking"
          />
        </div>
      </div>
    </div>
  );
}
